import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact' | 'text';
  className?: string;
}

const SwitcherContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: string }>`
  position: relative;
  display: flex;
  align-items: center;
`;

const LanguageButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'variant',
})<{ isOpen: boolean; variant: string }>`
  background-color: ${ThemeColors.background.paper};
  color: ${ThemeColors.text.primary};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  padding: ${props => 
    props.variant === 'compact' 
      ? `${Spacing.xs} ${Spacing.sm}` 
      : props.variant === 'text'
        ? `${Spacing.xs}`
        : `${Spacing.sm} ${Spacing.md}`
  };
  font-size: ${props => props.variant === 'compact' ? Typography.fontSize.sm : Typography.fontSize.base};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  min-width: ${props => props.variant === 'compact' ? '60px' : 'auto'};

  &:hover {
    background-color: ${ThemeColors.gray[100]};
    border-color: ${ThemeColors.primary.main};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemeColors.focus.ring};
  }
`;

const LanguageText = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: string }>`
  font-size: ${props => props.variant === 'compact' ? Typography.fontSize.sm : Typography.fontSize.base};
  font-weight: ${Typography.fontWeight.medium};
`;

const ChevronIcon = styled(ChevronDown).withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  color: ${ThemeColors.text.secondary};
`;

const DropdownMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + ${Spacing.xs});
  right: 0;
  background-color: ${ThemeColors.background.paper};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  box-shadow: ${Shadows.md};
  min-width: 80px;
  z-index: 1000;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  overflow: hidden;
`;

const DropdownItem = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  background-color: ${props => props.isActive ? ThemeColors.gray[50] : 'transparent'};
  color: ${props => props.isActive ? ThemeColors.primary.main : ThemeColors.text.primary};
  border: none;
  padding: ${Spacing.sm} ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${props => props.isActive ? Typography.fontWeight.medium : Typography.fontWeight.regular};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  text-align: left;
  width: 100%;

  &:hover {
    background-color: ${ThemeColors.gray[100]};
    color: ${ThemeColors.primary.main};
  }
  
  &:focus {
    outline: none;
    background-color: ${ThemeColors.gray[100]};
  }
`;

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'default',
  className
}) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLanguage = i18n.language;
  const isEnglish = currentLanguage === 'en';
  const displayText = variant === 'compact' ? (isEnglish ? 'EN' : 'FR') : (isEnglish ? 'English' : 'Français');

  const changeLanguage = (lang: 'en' | 'fr') => {
    i18n.changeLanguage(lang);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <SwitcherContainer variant={variant} className={className} ref={containerRef}>
      <LanguageButton 
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        isOpen={isOpen}
        variant={variant}
        aria-label={t('common.switchLanguage')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <LanguageText variant={variant}>{displayText}</LanguageText>
        <ChevronIcon isOpen={isOpen} aria-hidden="true" />
      </LanguageButton>
      <DropdownMenu isOpen={isOpen} role="menu">
        <DropdownItem
          type="button"
          isActive={currentLanguage === 'en'}
          onClick={() => changeLanguage('en')}
          role="menuitem"
        >
          EN
        </DropdownItem>
        <DropdownItem
          type="button"
          isActive={currentLanguage === 'fr'}
          onClick={() => changeLanguage('fr')}
          role="menuitem"
        >
          FR
        </DropdownItem>
      </DropdownMenu>
    </SwitcherContainer>
  );
};

export default LanguageSwitcher;
